import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const PrivacyPolicyPage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <div className="bg-[#f5f1e8] min-h-screen">
      <SEO
        title="Privacy Policy | Your Setl"
        description="Privacy Policy for Your Setl - Learn how we collect, use, and protect your personal information."
        url="https://www.yoursetl.co.uk/privacy-policy"
        noIndex={true}
      />

      {/* Header */}
      <section className="bg-[#3a2f28] py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1
            className="text-3xl md:text-4xl font-light tracking-wide text-[#f5f1e8] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Privacy Policy
          </h1>
          <p
            className="text-[#f5f1e8]/70"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Last updated: {new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto">
          <div
            className="prose prose-lg text-[#3a2f28]"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {/* Introduction */}
            <div className="mb-10">
              <h2
                className="text-2xl font-light text-[#3a2f28] mb-4"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Introduction
              </h2>
              <p className="text-base leading-relaxed text-[#3a2f28]/80 mb-4">
                Your Setl ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you visit our website yoursetl.co.uk.
              </p>
            </div>

            {/* Information We Collect */}
            <div className="mb-10">
              <h2
                className="text-2xl font-light text-[#3a2f28] mb-4"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Information We Collect
              </h2>
              
              <h3 className="text-lg font-medium text-[#3a2f28] mb-2 mt-6">Contact Form Information</h3>
              <p className="text-base leading-relaxed text-[#3a2f28]/80 mb-4">
                When you submit our contact form, we collect:
              </p>
              <ul className="list-disc pl-6 mb-4 text-[#3a2f28]/80 space-y-2">
                <li>Your name</li>
                <li>Email address</li>
                <li>Phone number (if provided)</li>
                <li>Your message</li>
              </ul>

              <h3 className="text-lg font-medium text-[#3a2f28] mb-2 mt-6">Analytics Information</h3>
              <p className="text-base leading-relaxed text-[#3a2f28]/80 mb-4">
                With your consent, we use Google Analytics (GA4) to collect anonymous information about how visitors use our website. This includes:
              </p>
              <ul className="list-disc pl-6 mb-4 text-[#3a2f28]/80 space-y-2">
                <li>Pages visited</li>
                <li>Time spent on pages</li>
                <li>How you arrived at our site</li>
                <li>Your general location (country/city level)</li>
                <li>Device and browser type</li>
              </ul>
            </div>

            {/* How We Use Your Information */}
            <div className="mb-10">
              <h2
                className="text-2xl font-light text-[#3a2f28] mb-4"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                How We Use Your Information
              </h2>
              <p className="text-base leading-relaxed text-[#3a2f28]/80 mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 mb-4 text-[#3a2f28]/80 space-y-2">
                <li>Respond to your enquiries about our country cabins</li>
                <li>Improve our website and user experience</li>
                <li>Understand how visitors interact with our site</li>
                <li>Send you information you've requested</li>
              </ul>
            </div>

            {/* Cookies */}
            <div className="mb-10">
              <h2
                className="text-2xl font-light text-[#3a2f28] mb-4"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Cookies
              </h2>
              <p className="text-base leading-relaxed text-[#3a2f28]/80 mb-4">
                Cookies are small text files stored on your device. We use the following types of cookies:
              </p>
              
              <h3 className="text-lg font-medium text-[#3a2f28] mb-2 mt-6">Essential Cookies</h3>
              <p className="text-base leading-relaxed text-[#3a2f28]/80 mb-4">
                These are necessary for the website to function and cannot be disabled. They include cookies that remember your cookie consent preferences.
              </p>

              <h3 className="text-lg font-medium text-[#3a2f28] mb-2 mt-6">Analytics Cookies (Optional)</h3>
              <p className="text-base leading-relaxed text-[#3a2f28]/80 mb-4">
                Google Analytics cookies help us understand how visitors use our website. These are only activated if you click "Accept Cookies" on our cookie banner. You can change your preference at any time by clearing your browser cookies and revisiting our site.
              </p>
            </div>

            {/* Your Rights */}
            <div className="mb-10">
              <h2
                className="text-2xl font-light text-[#3a2f28] mb-4"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Your Rights
              </h2>
              <p className="text-base leading-relaxed text-[#3a2f28]/80 mb-4">
                Under UK GDPR, you have the right to:
              </p>
              <ul className="list-disc pl-6 mb-4 text-[#3a2f28]/80 space-y-2">
                <li>Access the personal data we hold about you</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Withdraw consent for analytics cookies at any time</li>
                <li>Lodge a complaint with the Information Commissioner's Office (ICO)</li>
              </ul>
            </div>

            {/* Data Retention */}
            <div className="mb-10">
              <h2
                className="text-2xl font-light text-[#3a2f28] mb-4"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Data Retention
              </h2>
              <p className="text-base leading-relaxed text-[#3a2f28]/80 mb-4">
                Contact form submissions are retained for as long as necessary to respond to your enquiry and for our legitimate business purposes. Analytics data is retained for 14 months in accordance with Google Analytics default settings.
              </p>
            </div>

            {/* Contact Us */}
            <div className="mb-10">
              <h2
                className="text-2xl font-light text-[#3a2f28] mb-4"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Contact Us
              </h2>
              <p className="text-base leading-relaxed text-[#3a2f28]/80 mb-4">
                If you have any questions about this Privacy Policy or wish to exercise your rights, please contact us at:
              </p>
              <p className="text-base text-[#3a2f28]/80">
                <strong>Email:</strong>{' '}
                <a href="mailto:info@yoursetl.co.uk" className="text-[#d4a574] hover:underline">
                  info@yoursetl.co.uk
                </a>
              </p>
            </div>

            {/* Back Link */}
            <div className="mt-12 pt-8 border-t border-[#3a2f28]/20">
              <Link
                to="/"
                className="inline-block px-6 py-3 bg-[#3a2f28] text-[#f5f1e8] rounded hover:bg-[#2a231d] transition-colors duration-200"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
